import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, asArray, formatApiError } from "@/lib/api";
import { fmtTime } from "@/lib/time";
import { useAuth } from "@/context/AuthContext";
import { SeverityBadge, StatusBadge } from "@/components/Primitives";
import StatusSelect from "@/components/StatusSelect";
import AssigneeSelect from "@/components/AssigneeSelect";
import InsertPanel from "@/components/InsertPanel";
import AttachmentList from "@/components/AttachmentList";
import MergeTicketDialog from "@/components/MergeTicketDialog";
import AddChildDialog from "@/components/AddChildDialog";
import CustomFieldInputs from "@/components/CustomFieldInputs";
import {
  Sparkles, Send, Timer, Play, Square, GitMerge, Link2, Unlink, Plus,
  ThumbsUp, ThumbsDown, ListChecks, Star,
} from "lucide-react";
import { toast } from "sonner";

// Mirrors backend/attachments.py's UPLOAD_ROLES. super_admin/msp_owner pass
// every require_roles() check on the server regardless of this list, so they
// are included here too -- the upload control's presence must agree with
// what the server would actually allow.
const ATTACHMENT_UPLOAD_ROLES = [
  "l1_technician", "l2_technician", "l3_technician", "service_desk_manager",
  "super_admin", "msp_owner",
];

// Mirrors backend/core.py's TECH_ROLES, the set ticket_merge.py's
// require_roles(*TECH_ROLES) checks. super_admin/msp_owner pass that check
// unconditionally on the server, so they are included here too -- same
// reasoning as ATTACHMENT_UPLOAD_ROLES above.
const MERGE_ROLES = [
  "l1_technician", "l2_technician", "l3_technician", "service_desk_manager",
  "super_admin", "msp_owner",
];

export default function TicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canUploadAttachments = !!user && ATTACHMENT_UPLOAD_ROLES.includes(user.role);
  const canMerge = !!user && MERGE_ROLES.includes(user.role);
  const [t, setT] = useState(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [survivor, setSurvivor] = useState(null);   // the ticket THIS one merged into, for the banner
  const [userNames, setUserNames] = useState({});   // id -> name, to show who ran the merge
  const [body, setBody] = useState("");
  // Effort, never money: this platform carries no financial surface, so time is
  // recorded to answer operational questions - how long a category really takes,
  // whether a technician is saturated, which client absorbs the desk.
  const [time, setTime] = useState({ entries: [], total_minutes: 0, total_raw_minutes: 0 });
  const [mins, setMins] = useState("");
  const [tnote, setTnote] = useState("");
  const [timer, setTimer] = useState(null);      // the running timer, from the server
  const [skew, setSkew] = useState(0);           // server clock minus browser clock
  const [tick, setTick] = useState(0);
  const [workType, setWorkType] = useState("remote");
  const [workTypes, setWorkTypes] = useState([]);
  const [summary, setSummary] = useState("");
  const [rca, setRca] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  // Defaults to Internal. Today every note is customer-visible because the UI
  // never set this flag at all, though the backend supports it and the portal
  // filters on it. Defaulting to Internal makes the failure mode a note the
  // customer did not see, rather than one they should never have seen.
  const [internal, setInternal] = useState(true);
  const [tasks, setTasks] = useState([]);
  const commentRef = useRef(null);

  // Sub-project I, Task 9. A durable, page-level banner for the header's
  // status/priority/assignee/custom/link/approval actions -- a toast alone
  // auto-dismisses and, without `richColors` on this app's one global
  // <Toaster>, an error toast is the same neutral colour as a success one
  // (the exact defect `TicketFieldSettings.jsx`'s own review found and
  // fixed). Shared across those actions rather than one state per control:
  // they are mutually exclusive in time (a person does one at a time) and
  // a shared banner means only one place to look for "what just went
  // wrong", right under the ticket header where every one of them lives.
  const [actionErr, setActionErr] = useState("");

  // Custom fields (Task 3's `custom` sub-document, on screen for the first
  // time). `customDefs` is this ticket's CATEGORY's active definitions
  // (GET /ticket-field-defs/for-category -- Task 9's own finding: nothing
  // before this task exposed `defs_for` to a non-MANAGE caller at all).
  // `customValues` is the edit buffer, reseeded from the ticket's stored
  // `custom` every time the ticket reloads (never on every keystroke).
  const [customDefs, setCustomDefs] = useState([]);
  const [customValues, setCustomValues] = useState({});
  const [customErr, setCustomErr] = useState("");

  // Parent / children (Task 5/6's `parent_id`, on screen for the first
  // time). `parentTicket` is the resolved parent (number/title) when this
  // ticket IS a child -- a separate document, fetched separately. Children
  // arrive DIRECTLY on `t.children` (server.py's `get_ticket`, attached the
  // same way `approval_view` is) -- review finding: the first cut fetched
  // them from a standalone `/tickets/{id}/children` route that this page
  // never actually called correctly end to end, the same "capability with
  // no caller reaching it" shape this codebase keeps producing. No local
  // state for children any more -- `t.children` (always an array, never a
  // missing key) IS the state, so it can never drift from what was last
  // loaded.
  const [parentTicket, setParentTicket] = useState(null);
  const [addChildOpen, setAddChildOpen] = useState(false);

  // Change approval (Task 7's engine, on screen for the first time).
  const [decisionNote, setDecisionNote] = useState("");
  const [decisionBusy, setDecisionBusy] = useState(false);

  const load = () => api.get(`/tickets/${id}`).then(r => setT(r.data));
  useEffect(() => { load(); }, [id]);

  // Reseed the custom-fields edit buffer from the SERVER's own value every
  // time the ticket document itself changes (initial load, or after a
  // save/reload elsewhere on the page) -- never on a re-render that leaves
  // `t` the same object, so a half-typed edit is not clobbered by, say,
  // the timer's own 1-second tick re-render.
  useEffect(() => { setCustomValues(t?.custom || {}); }, [t]);

  // Active definitions for THIS ticket's category -- refetched only when
  // the category itself changes (it never does, on an existing ticket, but
  // `t` reloading is cheap insurance against a stale list either way).
  useEffect(() => {
    if (!t?.category) { setCustomDefs([]); return; }
    api.get("/ticket-field-defs/for-category", { params: { category: t.category } })
      .then((r) => setCustomDefs(asArray(r.data)))
      .catch(() => setCustomDefs([]));
  }, [t?.category]);

  // Parent shown as a link -- fetch its number/title (a separate document;
  // `t` itself only carries the parent's id). Never needed when this
  // ticket has no parent (one level of nesting only, so a ticket with a
  // parent can have no children of its own, and `t.children` is simply
  // empty in that case -- no fetch to skip on that side).
  useEffect(() => {
    if (!t?.parent_id) { setParentTicket(null); return; }
    api.get(`/tickets/${t.parent_id}`).then((r) => setParentTicket(r.data)).catch(() => setParentTicket(null));
  }, [t?.parent_id]);

  // GET /tickets/{id} still returns a merged ticket -- deliberately, so this
  // page can render the banner below -- but it carries no NUMBER or TITLE
  // for the survivor, only its id. Fetch it separately once we know we need
  // it. A portal account viewing its own merged ticket can still read the
  // survivor (same client, by construction of the merge itself).
  useEffect(() => {
    if (!t?.merged_into) { setSurvivor(null); return; }
    api.get(`/tickets/${t.merged_into}`).then(r => setSurvivor(r.data)).catch(() => setSurvivor(null));
    /* eslint-disable-next-line */
  }, [t?.merged_into]);

  // Resolve `merged_by` to a name for the banner. A portal account is
  // refused /users (403); degrade to no name rather than breaking the page.
  useEffect(() => {
    api.get("/users").then(r => setUserNames(Object.fromEntries(asArray(r.data).map((u) => [u.id, u.name]))))
      .catch(() => setUserNames({}));
    /* eslint-disable-next-line */
  }, []);

  const loadTasks = () => api.get(`/tickets/${id}/tasks`)
    .then((r) => setTasks(asArray(r.data))).catch(() => setTasks([]));
  useEffect(() => { loadTasks(); /* eslint-disable-next-line */ }, [id]);

  const toggleTask = async (task) => {
    await api.patch(`/tickets/${id}/tasks/${task.id}`, { done: !task.done });
    loadTasks();
  };

  const loadTime = async () => {
    try { const { data } = await api.get(`/tickets/${id}/time`); setTime(data); }
    catch { /* a ticket with no time is not an error */ }
  };
  useEffect(() => { loadTime(); /* eslint-disable-next-line */ }, [id]);

  const loadTimer = async () => {
    try {
      const { data } = await api.get(`/tickets/${id}/timer`);
      setTimer(data.running ? data.timer : null);
      // Tick against the SERVER's clock. A browser with a skewed clock must not
      // display — or bill — a different duration from the one the server records.
      if (data.server_now) setSkew(new Date(data.server_now).getTime() - Date.now());
    } catch { /* a ticket with no timer is not an error */ }
  };
  useEffect(() => {
    loadTimer();
    api.get("/desk/work-types").then(r => setWorkTypes(r.data?.work_types || [])).catch(() => {});
    /* eslint-disable-next-line */
  }, [id]);
  useEffect(() => {
    if (!timer) return;
    const h = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(h);
  }, [timer]);

  const elapsed = () => {
    if (!timer) return "0:00:00";
    const ms = (Date.now() + skew) - new Date(timer.started_at).getTime();
    const s = Math.max(0, Math.floor(ms / 1000));
    return `${Math.floor(s / 3600)}:${String(Math.floor(s / 60) % 60).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  };

  const startTimer = async () => {
    try {
      const { data } = await api.post(`/tickets/${id}/timer/start`, { work_type: workType });
      setTimer(data.timer);
      if (data.auto_stopped) {
        // Say so plainly. Two timers running at once would bill the same
        // wall-clock hour to two different clients.
        toast.warning(`Your timer on the other ticket was stopped and logged as ${data.auto_stopped.minutes} min.`);
      } else if (data.already_running) {
        toast.info("Already running on this ticket");
      } else {
        toast.success("Timer started");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start the timer"); }
  };

  const stopTimer = async () => {
    try {
      const { data } = await api.post(`/tickets/${id}/timer/stop`, {
        note: tnote, work_type: workType });
      setTimer(null); setTnote(""); loadTime();
      toast.success(`${data.raw_minutes} min worked, ${data.minutes} min billed` +
        (data.billable ? ` (${data.currency} ${data.amount})` : " (non-chargeable)"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not stop the timer"); }
  };

  const addTime = async () => {
    const n = parseInt(mins, 10);
    if (!n || n <= 0) { toast.error("Enter the minutes spent"); return; }
    try {
      const { data } = await api.post(`/tickets/${id}/time`,
        { minutes: n, note: tnote, work_type: workType });
      setMins(""); setTnote(""); loadTime();
      toast.success(`${data.raw_minutes} min worked, ${data.minutes} min billed` +
        (data.billable ? ` (${data.currency} ${data.amount})` : " (non-chargeable)"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not log time"); }
  };

  const insertAtCursor = (text) => {
    const el = commentRef.current;
    if (!el) { setBody((b) => b + text); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + text + body.slice(end);
    setBody(next);
    // Wait for React to flush the new value into the DOM before moving the
    // caret and refocusing, or the browser's own (stale) selection wins.
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + text.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const addComment = async () => {
    if (!body.trim()) return;
    try {
      await api.post(`/tickets/${id}/comments`, { body, internal });
      setBody("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const change = async (field, val) => {
    setActionErr("");
    try {
      await api.patch(`/tickets/${id}`, { [field]: val });
      toast.success("Updated");
      load();
    } catch (e) {
      // Durable: a status change is exactly what Task 7's approval gate
      // (guard 5) can now 409 -- "requires approval before it can leave
      // 'open'" is a message a technician needs to still see a moment
      // later, not one that vanished with a toast.
      setActionErr(formatApiError(e));
      toast.error(formatApiError(e));
    }
  };

  // Only the keys that actually changed, using an explicit `null` to clear
  // a field the operator emptied out -- the server's own per-key clear
  // semantics (`update_ticket`'s guard 3; `{"custom": null}` clearing
  // EVERYTHING is a different, refused shape and never what an emptied
  // single input means).
  const saveCustom = async () => {
    setCustomErr("");
    const stored = t.custom || {};
    const diff = {};
    for (const d of customDefs) {
      const nv = customValues[d.key];
      const ov = stored[d.key];
      const emptied = nv === "" || nv === undefined;
      if (emptied) {
        if (ov !== undefined) diff[d.key] = null;
        continue;
      }
      if (nv !== ov) diff[d.key] = nv;
    }
    if (!Object.keys(diff).length) { toast.info("No changes to save"); return; }
    try {
      await api.patch(`/tickets/${id}`, { custom: diff });
      toast.success("Custom fields updated");
      load();
    } catch (e) {
      setCustomErr(formatApiError(e));
      toast.error(formatApiError(e));
    }
  };

  const unlinkParent = async () => {
    setActionErr("");
    try {
      await api.delete(`/tickets/${id}/parent`);
      toast.success("Unlinked from parent");
      load();
    } catch (e) {
      setActionErr(formatApiError(e));
      toast.error(formatApiError(e));
    }
  };

  const unlinkChild = async (childId, childNumber) => {
    setActionErr("");
    try {
      await api.delete(`/tickets/${childId}/parent`);
      toast.success(`#${childNumber} unlinked`);
      // `children` lives on `t` now (server-attached, like `approval_view`)
      // -- reload the parent rather than hand-filtering a local copy, so
      // there is exactly one place this list is ever computed.
      load();
    } catch (e) {
      setActionErr(formatApiError(e));
      toast.error(formatApiError(e));
    }
  };

  const decide = async (action) => {
    setActionErr("");
    setDecisionBusy(true);
    try {
      await api.post(`/tickets/${id}/${action}`,
        decisionNote.trim() ? { note: decisionNote.trim() } : {});
      toast.success(action === "approve" ? "Approved" : "Rejected");
      setDecisionNote("");
      load();
    } catch (e) {
      // Durable: the server refuses regardless of what this page happened
      // to show -- see `can_approve`/`can_reject` in the header note below.
      // A stale page (the ticket was decided by someone else a moment ago,
      // or the caller was quietly dropped from `approver_ids`) must
      // surface that clearly, not as a toast that is gone before it is
      // read.
      setActionErr(formatApiError(e));
      toast.error(formatApiError(e));
    } finally {
      setDecisionBusy(false);
    }
  };

  const aiSummary = async () => { setLoadingAI(true); try { const r = await api.post(`/ai/ticket-summary/${id}`); setSummary(r.data.summary); } finally { setLoadingAI(false); } };
  const aiRca = async () => { setLoadingAI(true); try { const r = await api.post(`/ai/rca/${id}`); setRca(r.data); } finally { setLoadingAI(false); } };

  if (!t) return <div className="text-zinc-500 text-sm">Loading…</div>;

  // A merged ticket is a closed duplicate: the API still returns it (so this
  // banner can exist), but nothing here should look editable, because
  // everything that was ever said or billed against it now lives on the
  // survivor.
  const merged = !!t.merged_into;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <div className="xl:col-span-2 space-y-4">
        {merged && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-md p-4 text-sm text-blue-200"
            data-testid="ticket-merged-banner">
            This ticket was merged into{" "}
            <Link to={`/tickets/${t.merged_into}`} className="font-mono underline hover:text-blue-100"
              data-testid="ticket-merged-link">
              #{survivor?.number ?? "…"}{survivor?.title ? ` — ${survivor.title}` : ""}
            </Link>
            {t.merged_at && <> on <span className="font-mono">{fmtTime(t.merged_at)}</span></>}
            {t.merged_by && <> by {userNames[t.merged_by] || <span className="font-mono">{t.merged_by}</span>}</>}.
            {" "}Its comments, attachments and time now live there. This ticket is read-only.
          </div>
        )}
        {actionErr && (
          <div className="bg-red-950/30 border border-red-900/50 rounded-md p-4 text-sm text-red-300"
            data-testid="ticket-action-error">
            {actionErr}
          </div>
        )}
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
          <div className="flex items-center gap-3">
            <div className="text-zinc-500 font-mono text-sm">#{t.number}</div>
            <SeverityBadge level={t.priority} />
            <StatusBadge status={t.status} />
            {t.vip && (
              <span data-testid="ticket-vip-badge"
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium bg-amber-500/15 text-amber-300 border border-amber-500/30">
                <Star size={11} fill="currentColor" /> VIP
              </span>
            )}
            <span className="text-xs text-zinc-500 ml-auto font-mono">SLA: {t.sla_deadline ? fmtTime(t.sla_deadline) : "—"}</span>
            {canMerge && !merged && (
              <button onClick={() => setMergeOpen(true)} data-testid="ticket-merge-btn"
                className="flex items-center gap-1.5 border border-zinc-800 hover:border-zinc-600 text-zinc-300 rounded px-2.5 py-1 text-xs">
                <GitMerge size={13} /> Merge
              </button>
            )}
          </div>
          <h1 className="font-display text-2xl font-semibold tracking-tight mt-3">{t.title}</h1>
          <p className="text-sm text-zinc-400 mt-2">{t.description}</p>
          {merged ? (
            <div className="mt-4 text-xs text-zinc-500">
              Priority and assignee are no longer editable on a merged ticket.
            </div>
          ) : (
            <div className="mt-4 flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Status</span>
                <StatusSelect value={t.status} onChange={(v) => change("status", v)} testid="ticket-status-update" />
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Priority</span>
                <select data-testid="ticket-priority-update" value={t.priority}
                  onChange={(e) => change("priority", e.target.value)}
                  className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs">
                  {["critical", "high", "medium", "low"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2 min-w-0 flex-1 max-w-xs">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono shrink-0">Assignee</span>
                <AssigneeSelect value={t.assignee_id} includeAuto={false}
                  onChange={(v) => change("assignee_id", v)} testid="ticket-assignee-update" />
              </div>
            </div>
          )}
        </div>

        {t.approval_view?.category_requires_approval && !merged && (
          <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5" data-testid="ticket-approval-card">
            <div className="flex items-center gap-2 mb-3">
              <ListChecks size={14} className="text-zinc-400" />
              <div className="font-display font-medium">Change Approval</div>
              {/* Task 11 fix, found by seeding a real approval rule for the
                  first time. This card renders whenever the CATEGORY carries
                  a rule -- `category_requires_approval`, which is a fact
                  about the category, not about this ticket. With no rule
                  anywhere in the database, this card had only ever been seen
                  on `open` tickets, in tests. The moment a rule exists on a
                  category with history behind it, every RESOLVED and CLOSED
                  ticket in that category rendered "AWAITING DECISION" in
                  amber and, below, "Waiting on a listed approver" -- on a
                  finished job. A ticket that is no longer `open` is past the
                  gate by definition (`approvals.refuse_if_leaving_open`
                  returns immediately for it, and `decision_view` forces
                  `can_approve`/`can_reject` false), so it is not awaiting
                  anything; it simply has no decision recorded, which is the
                  ordinary state of a ticket that moved before the rule
                  existed. */}
              <span className="ml-auto">
                <SeverityBadge
                  level={t.approval_view.state === "approved" ? "success"
                    : t.approval_view.state === "rejected" ? "critical"
                    : t.status === "open" ? "medium" : "info"}>
                  {t.approval_view.state
                    || (t.status === "open" ? "awaiting decision" : "no decision recorded")}
                </SeverityBadge>
              </span>
            </div>
            {t.approval_view.state && (
              <div className="text-xs text-zinc-500 mb-3">
                {t.approval_view.decided_at && <>Decided <span className="font-mono">{fmtTime(t.approval_view.decided_at)}</span></>}
                {t.approval_view.note && <> — "{t.approval_view.note}"</>}
              </div>
            )}
            {/* Review IMPORTANT fix: `can_approve`/`can_reject` are now
                independent -- a listed approver may REJECT an
                already-approved-but-still-open ticket (a legitimate
                "change my mind" reversal, unaffected by the double-approve
                guard) even when they may no longer APPROVE it again. A
                single `can_decide` boolean hid that reachable write. */}
            {(t.approval_view.can_approve || t.approval_view.can_reject) ? (
              <div className="space-y-2" data-testid="ticket-approval-controls">
                <textarea value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)}
                  rows={2} placeholder="Optional note…" data-testid="ticket-approval-note"
                  className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
                <div className="flex gap-2">
                  {t.approval_view.can_approve && (
                    <button onClick={() => decide("approve")} disabled={decisionBusy} data-testid="ticket-approve-btn"
                      className="flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 text-white rounded px-3 py-1.5 text-sm">
                      <ThumbsUp size={13} /> Approve
                    </button>
                  )}
                  {t.approval_view.can_reject && (
                    <button onClick={() => decide("reject")} disabled={decisionBusy} data-testid="ticket-reject-btn"
                      className="flex items-center gap-1.5 bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white rounded px-3 py-1.5 text-sm">
                      <ThumbsDown size={13} /> Reject
                    </button>
                  )}
                </div>
              </div>
            ) : (
              t.approval_view.state !== "approved" && (
                <div className="text-xs text-zinc-500" data-testid="ticket-approval-waiting">
                  This category requires approval before a ticket can leave "open".
                  {t.approval_view.state === "rejected"
                    ? " A listed approver must approve it before it can proceed."
                    : t.status === "open"
                      ? " Waiting on a listed approver."
                      : " No decision is recorded against this one."}
                </div>
              )
            )}
          </div>
        )}

        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5" data-testid="ticket-links-card">
          <div className="flex items-center gap-2 mb-3">
            <Link2 size={14} className="text-zinc-400" />
            <div className="font-display font-medium">Linked Tickets</div>
            {!merged && !t.parent_id && (
              <button onClick={() => setAddChildOpen(true)} data-testid="add-child-btn"
                className="ml-auto flex items-center gap-1.5 border border-zinc-800 hover:border-zinc-600 text-zinc-300 rounded px-2.5 py-1 text-xs">
                <Plus size={13} /> Add child
              </button>
            )}
          </div>
          {t.parent_id ? (
            <div className="flex items-center gap-2 text-sm" data-testid="ticket-parent-link">
              <span className="text-zinc-500">Parent:</span>
              {parentTicket ? (
                <Link to={`/tickets/${parentTicket.id}`} className="font-mono text-blue-400 hover:underline truncate">
                  #{parentTicket.number} — {parentTicket.title}
                </Link>
              ) : <span className="text-zinc-500">…</span>}
              {!merged && (
                <button onClick={unlinkParent} title="Unlink from parent" data-testid="unlink-parent-btn"
                  className="ml-auto p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-red-400 shrink-0">
                  <Unlink size={13} />
                </button>
              )}
            </div>
          ) : (t.children || []).length > 0 ? (
            <div className="space-y-1.5" data-testid="ticket-children-list">
              {t.children.map((c) => (
                <div key={c.id} className="flex items-center gap-2 text-sm min-w-0" data-testid={`ticket-child-${c.id}`}>
                  <Link to={`/tickets/${c.id}`} className="font-mono text-blue-400 hover:underline truncate min-w-0">
                    #{c.number} — {c.title}
                  </Link>
                  <StatusBadge status={c.status} />
                  {!merged && (
                    <button onClick={() => unlinkChild(c.id, c.number)} title="Unlink this child"
                      data-testid={`unlink-child-${c.id}`}
                      className="ml-auto p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-red-400 shrink-0">
                      <Unlink size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-zinc-500 text-sm" data-testid="ticket-no-links">No linked tickets.</div>
          )}
        </div>

        {customDefs.length > 0 && (
          <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5" data-testid="ticket-custom-fields-card">
            <div className="font-display font-medium mb-3">Custom Fields</div>
            {customErr && (
              <div className="mb-3 border border-red-900/50 bg-red-950/30 text-red-300 rounded px-3 py-2 text-sm"
                data-testid="custom-fields-error">
                {customErr}
              </div>
            )}
            <CustomFieldInputs defs={customDefs} values={customValues} disabled={merged}
              onChange={(k, v) => setCustomValues((c) => ({ ...c, [k]: v }))} />
            {!merged && (
              <button onClick={saveCustom} data-testid="save-custom-fields-btn"
                className="mt-3 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded px-3 py-1.5">
                Save custom fields
              </button>
            )}
          </div>
        )}

        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
          <div className="font-display font-medium mb-3">Conversation</div>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {(t.comments || []).map(c => (
              <div key={c.id} className="border border-zinc-900 rounded p-3 bg-zinc-900/30">
                <div className="flex items-center gap-2 text-xs text-zinc-500"><span className="text-zinc-300 font-medium">{c.actor_name}</span><span className="font-mono">{c.at ? fmtTime(c.at) : ""}</span>{c.internal && <SeverityBadge level="info">internal</SeverityBadge>}</div>
                <div className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap">{c.body}</div>
              </div>
            ))}
            {(t.comments || []).length === 0 && <div className="text-zinc-500 text-sm">No comments yet.</div>}
          </div>
          {merged ? (
            <div className="mt-3 text-xs text-zinc-500 border-t border-zinc-900 pt-3">
              This ticket is merged and read-only. Reply on{" "}
              <Link to={`/tickets/${t.merged_into}`} className="text-blue-400 hover:underline">
                #{survivor?.number ?? t.merged_into}
              </Link> instead.
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              <div className="flex gap-1 items-center" data-testid="comment-visibility">
                {[[true, "Internal note", "amber"], [false, "Reply to customer", "blue"]].map(([v, label, tone]) => (
                  <button key={String(v)} onClick={() => setInternal(v)}
                    data-testid={`comment-visibility-${v ? "internal" : "public"}`}
                    className={`px-3 py-1 text-xs rounded border ${internal === v
                      ? (tone === "amber"
                          ? "bg-amber-500/15 border-amber-500/40 text-amber-300"
                          : "bg-blue-500/15 border-blue-500/40 text-blue-300")
                      : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}>{label}</button>
                ))}
                <div className="ml-auto">
                  <InsertPanel ticketId={id} onInsert={insertAtCursor} />
                </div>
              </div>
              <div className="flex gap-2">
                <textarea data-testid="ticket-comment-input" value={body} onChange={(e) => setBody(e.target.value)}
                  ref={commentRef}
                  rows={2} placeholder={internal ? "Internal note — the customer will not see this…" : "Reply — the customer will see this…"}
                  className="flex-1 min-w-0 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
                <button data-testid="ticket-comment-submit" onClick={addComment}
                  className="bg-blue-600 hover:bg-blue-700 rounded px-3 text-white text-sm flex items-center gap-1 shrink-0">
                  <Send size={14} /> Send
                </button>
              </div>
              {!internal && !t.first_response_at && (
                <div className="text-[11px] text-emerald-400/80 font-mono">
                  This reply will stop the first-response SLA clock.
                </div>
              )}
            </div>
          )}
        </div>

        <AttachmentList ticketId={id} canWrite={canUploadAttachments && !merged} />
      </div>

      <div className="space-y-4">
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
          <div className="flex items-center gap-2 mb-3"><Sparkles size={14} className="text-blue-400" /><div className="font-display font-medium">AI Operations</div></div>
          <div className="flex gap-2 flex-wrap mb-3">
            <button data-testid="ai-summary-btn" onClick={aiSummary} disabled={loadingAI} className="bg-blue-600 hover:bg-blue-700 text-white text-xs rounded px-3 py-1.5">Summarize</button>
            <button data-testid="ai-rca-btn" onClick={aiRca} disabled={loadingAI} className="bg-zinc-900 border border-zinc-800 text-zinc-100 text-xs rounded px-3 py-1.5">Root Cause</button>
          </div>
          {summary && <pre className="text-xs whitespace-pre-wrap text-zinc-300 leading-relaxed max-h-64 overflow-auto border border-zinc-900 rounded p-3 mb-3" data-testid="ai-summary-text">{summary}</pre>}
          {rca && (
            <div className="space-y-2">
              <pre className="text-xs whitespace-pre-wrap text-zinc-300 leading-relaxed max-h-72 overflow-auto border border-zinc-900 rounded p-3">{rca.rca}</pre>
              {rca.related_tickets?.length > 0 && (
                <div className="text-[11px] text-zinc-500 font-mono">{rca.related_tickets.length} related historical tickets analyzed.</div>
              )}
            </div>
          )}
        </div>
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5 mb-4" data-testid="ticket-time">
          <div className="flex items-center gap-2 mb-1">
            <Timer size={14} className="text-zinc-400" />
            <div className="font-display text-sm text-zinc-200">Time on this ticket</div>
            <div className="ml-auto font-mono text-sm text-zinc-100 text-right">
              <div>{Math.floor(time.total_minutes / 60)}h {time.total_minutes % 60}m
                <span className="text-zinc-600 text-[10px] ml-1">billed</span></div>
              <div className="text-zinc-500 text-[11px]">
                {Math.floor(time.total_raw_minutes / 60)}h {time.total_raw_minutes % 60}m actual
              </div>
            </div>
          </div>
          <div className="space-y-1.5 mt-3 max-h-44 overflow-y-auto">
            {time.entries.map(e => (
              <div key={e.id} className="text-[12px] text-zinc-400 flex gap-2">
                <span className="font-mono text-zinc-300 shrink-0">{e.minutes}m</span>
                <span className="min-w-0">
                  {e.note || e.work_type_name}
                  <span className="text-zinc-600"> - {e.user_name}</span>
                </span>
              </div>
            ))}
            {!time.entries.length && <div className="text-zinc-600 text-[12px]">No time logged yet.</div>}
          </div>
          {merged ? (
            <div className="mt-3 text-[11px] text-zinc-600">
              Time tracking is closed on a merged ticket. Log further time on the survivor.
            </div>
          ) : (
            <>
              {/* start / stop */}
              <div className="mt-3 rounded border border-zinc-900 bg-zinc-900/40 p-3">
                <div className="flex items-center gap-2">
                  {timer ? (
                    <>
                      <span className="font-mono text-lg text-emerald-400 tabular-nums">{elapsed()}</span>
                      <span className="text-[11px] text-zinc-500">running</span>
                      <button onClick={stopTimer} data-testid="timer-stop"
                        className="ml-auto flex items-center gap-1.5 bg-red-600 hover:bg-red-500 text-white rounded px-3 py-1.5 text-sm">
                        <Square size={13} /> Stop
                      </button>
                    </>
                  ) : (
                    <>
                      <select value={workType} onChange={e => setWorkType(e.target.value)}
                        data-testid="timer-worktype"
                        className="flex-1 min-w-0 bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 outline-none">
                        {workTypes.map(w => (
                          <option key={w.key} value={w.key}>
                            {w.name}{w.billable ? "" : " (non-chargeable)"}
                          </option>
                        ))}
                      </select>
                      <button onClick={startTimer} data-testid="timer-start"
                        className="shrink-0 flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-600 text-white rounded px-3 py-1.5 text-sm">
                        <Play size={13} /> Start
                      </button>
                    </>
                  )}
                </div>
                {timer && (
                  <input value={tnote} onChange={e => setTnote(e.target.value)}
                    placeholder="what you are doing (saved when you stop)"
                    data-testid="timer-note"
                    className="mt-2 w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 outline-none focus:border-zinc-600" />
                )}
              </div>

              <div className="mt-3 space-y-2">
                <div className="flex gap-2">
                  <input value={mins} onChange={e => setMins(e.target.value)} placeholder="min"
                    data-testid="time-minutes" inputMode="numeric"
                    className="w-16 shrink-0 bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 outline-none focus:border-zinc-600" />
                  <select value={workType} onChange={e => setWorkType(e.target.value)}
                    data-testid="time-worktype"
                    className="flex-1 min-w-0 bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 outline-none">
                    {workTypes.map(w => (
                      <option key={w.key} value={w.key}>
                        {w.name}{w.billable ? "" : " (non-chargeable)"}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <input value={tnote} onChange={e => setTnote(e.target.value)} placeholder="what was done"
                    data-testid="time-note"
                    className="flex-1 min-w-0 bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 outline-none focus:border-zinc-600" />
                  <button onClick={addTime} data-testid="time-submit"
                    className="shrink-0 bg-blue-600 hover:bg-blue-700 rounded px-3 text-white text-sm">Log</button>
                </div>
              </div>
            </>
          )}
        </div>

        {tasks.length > 0 && (
          <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5" data-testid="ticket-tasks">
            <div className="flex items-center gap-2 mb-3">
              <div className="font-display font-medium">Checklist</div>
              <div className="ml-auto font-mono text-xs text-zinc-500">
                {tasks.filter((x) => x.done).length} / {tasks.length}
              </div>
            </div>
            <div className="space-y-1.5">
              {tasks.map((task) => (
                <label key={task.id} data-testid={`ticket-task-${task.id}`}
                  className={`flex items-start gap-2 text-sm group ${merged ? "cursor-default" : "cursor-pointer"}`}>
                  <input type="checkbox" checked={!!task.done} disabled={merged} onChange={() => toggleTask(task)}
                    className="mt-0.5 accent-blue-500" />
                  <span className={task.done ? "text-zinc-600 line-through" : "text-zinc-300"}>
                    {task.name}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5 text-xs space-y-2 text-zinc-400">
          <div className="font-display text-sm text-zinc-200 mb-2">Context</div>
          <div>Requester: <span className="font-mono">{t.requester_name == null ? "—" : t.requester_name}</span></div>
          <div>Category: <span className="font-mono">{t.category}</span></div>
          <div>Client: <span className="font-mono">{t.client_id}</span></div>
          {t.asset_id && <div>Asset: <Link to={`/assets/${t.asset_id}`} className="font-mono text-blue-400 hover:underline">{t.asset_id}</Link></div>}
          <div>Created: <span className="font-mono">{t.created_at ? fmtTime(t.created_at) : "—"}</span></div>
        </div>
      </div>

      {mergeOpen && (
        <MergeTicketDialog
          ticket={t}
          onClose={() => setMergeOpen(false)}
          onMerged={() => { setMergeOpen(false); load(); }}
        />
      )}

      {addChildOpen && (
        <AddChildDialog
          ticket={t}
          onClose={() => setAddChildOpen(false)}
          onLinked={() => { setAddChildOpen(false); load(); }}
        />
      )}
    </div>
  );
}
